import { Card, CardContent } from "@material-ui/core";
import { AxiosError } from "axios";
import classnames from "classnames";
import { parseISO } from "date-fns";
import { useMemo } from "react";
import { useCallback } from "react";
import { useNotify, useTranslate } from "react-admin";
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import { handleAxiosError } from "../../../../../common/data/axiosErrorHandler";
import {
  DriverLogDetailedView,
  EventApiFactory,
  ShiftEventsDirection,
  ShiftEventsInputDto,
} from "../../../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../../../common/hooks/useAxiosInstance";
import { CompactFormToolbar } from "../../../../common/form/CompactFormToolbar";
import { CompactSaveButton } from "../../../../common/form/CompactSaveButton";
import { HookFormEventPicker } from "../../../../common/form/hook-form/inputs/HookFormEventPicker";
import { HookFormSelectInput } from "../../../../common/form/hook-form/inputs/HookFormSelectInput";
import { HookFormTextInput } from "../../../../common/form/hook-form/inputs/HookFormTextInput";

interface Props {
  record: DriverLogDetailedView;
  onSuccess: () => void;
  onFailed?: () => void;
}

const DefaultCompactFormToolbar = () => {
  const {
    formState: { isSubmitting, isValid },
  } = useFormContext();
  return (
    <CompactFormToolbar>
      <CompactSaveButton
        saving={isSubmitting}
        disabled={isSubmitting || !isValid}
      />
    </CompactFormToolbar>
  );
};

export const ShiftEventsForm = ({
  record,
  onSuccess,
  onFailed,
  ...rest
}: Props) => {
  const translate = useTranslate();
  const notify = useNotify();
  const axios = useAxiosInstance();
  const onSubmit = useCallback(
    (data: ShiftEventsInputDto) => {
      if (!axios) {
        return;
      }

      return EventApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppEventShifteventsPost(data)
        .then(() => {
          onSuccess();
        })
        .catch((e: AxiosError) => {
          if (onFailed) {
            onFailed();
          }

          notify(handleAxiosError(e), { type: "error" });
        });
    },
    [axios, notify, onSuccess, onFailed]
  );

  const formContext = useForm<ShiftEventsInputDto>({
    mode: "onChange",
  });

  const { handleSubmit, control } = formContext;

  const handleSubmitInternal: SubmitHandler<ShiftEventsInputDto> = (data) => {
    return Promise.resolve(onSubmit(data)).catch((e: Error) => {
      notify(e.message ?? "An unexpected error has occurred", "error");
    });
  };

  const logDate = useMemo(
    () => (record.date ? parseISO(record.date) : undefined),
    [record.date]
  );

  return (
    <CardContent>
      <FormProvider {...formContext}>
        <form
          className={classnames("simple-form", "form-create-edit-drawer")}
          {...sanitizeRestProps(rest)}
          onSubmit={handleSubmit(handleSubmitInternal, (err) =>
            console.error(err)
          )}>
          <Card>
            <CardContent>
              <div className="ra-input">
                <HookFormEventPicker<ShiftEventsInputDto>
                  control={control}
                  name="startEvent"
                  label="resources.hos-driver-log-view.labels.startEvent"
                  rules={{ required: translate("ra.validation.required") }}
                  variant="outlined"
                  defaultValue=""
                  initialDate={logDate}
                  driverId={record.driverId!}
                  timeZone={record.homeTerminalTimeZone!}
                />
              </div>
              <div className="ra-input">
                <HookFormEventPicker<ShiftEventsInputDto>
                  control={control}
                  name="endEvent"
                  label="resources.hos-driver-log-view.labels.endEvent"
                  rules={{ required: translate("ra.validation.required") }}
                  variant="outlined"
                  defaultValue=""
                  initialDate={logDate}
                  driverId={record.driverId!}
                  timeZone={record.homeTerminalTimeZone!}
                />
              </div>
              <div className="ra-input">
                <HookFormSelectInput<ShiftEventsInputDto>
                  control={control}
                  name="direction"
                  choices={[
                    { id: ShiftEventsDirection.Past, name: "Shift backward" },
                    { id: ShiftEventsDirection.Future, name: "Shift forward" },
                  ]}
                  label="Shift direction"
                  rules={{ required: translate("ra.validation.required") }}
                  variant="outlined"
                  defaultValue=""
                />
              </div>
              <div className="ra-input">
                <HookFormTextInput<ShiftEventsInputDto>
                  control={control}
                  name="time"
                  label="Time (hh:mm)"
                  rules={{
                    required: translate("ra.validation.required"),
                    pattern: {
                      value: /^\d+:[0-5]\d$/,
                      message: "Invalid time",
                    },
                  }}
                  variant="outlined"
                  defaultValue=""
                  mask="99:99"
                />
              </div>
            </CardContent>
          </Card>
          <DefaultCompactFormToolbar />
        </form>
      </FormProvider>
    </CardContent>
  );
};

const sanitizeRestProps = ({
  active,
  dirty,
  dirtyFields,
  dirtyFieldsSinceLastSubmit,
  dirtySinceLastSubmit,
  error,
  errors,
  form,
  hasSubmitErrors,
  hasValidationErrors,
  initialValues,
  modified = null,
  modifiedSinceLastSubmit,
  save = null,
  submitError,
  submitErrors,
  submitFailed,
  submitSucceeded,
  submitting,
  touched = null,
  valid,
  values,
  visited = null,
  __versions = null,
  ...props
}: any) => props;
